import {ApiService} from '../api';
import axios, {AxiosResponse} from 'axios';
import {SectionTypes} from '../../utils/enums';
import {TranslateItemDTO} from "../../types/DTOs/TranslateItemDTO";
import {TranslateItemResponseDTO} from "../../types/DTOs/TranslateItemResponseDTO";
import {TranslateBulkDTO} from "../../types/DTOs/TranslateBulkDTO";
import {TranslatedLanguagesMapDTO} from "../../types/DTOs/TranslatedLanguagesMapDTO";
import {bool} from "yup";
import {EducationTranDTO} from "../../types/DTOs/Translation/EducationTranDTO";
import {CertificationTranDTO} from "../../types/DTOs/Translation/CertificationTranDTO";
import {ProjectTranDTO} from "../../types/DTOs/Translation/ProjectTranDTO";
import {SkillTranDTO} from "../../types/DTOs/Translation/SkillTranDTO";
import {WorkExperienceTranDTO} from "../../types/DTOs/Translation/WorkExperienceTranDTO";

export class TranslationAPI extends ApiService {
    private constructor() {
        super();
    }

    /**
     * Traduce un singolo elemento.
     * POST /api/translations/translate
     */
    async translateItem(data: TranslateItemDTO): Promise<boolean> {
        let url: string = '';
        switch (data.sectionType) {
            case SectionTypes.CERTIFICATION:
                url = '/translate/certification';
                break;
            case SectionTypes.EDUCATION:
                url = '/translate/education';
                break;
            case SectionTypes.EXPERIENCE:
                url = '/translate/workexperience';
                break;
            case SectionTypes.SKILL:
                url = '/translate/skill';
                break;
            case SectionTypes.PROJECT:
                url = '/translate/project';
                break;
        }

        if (url != '') {
            const response: AxiosResponse<boolean> =
                await this.api.post(url, data, {
                    timeout: 60000 // ← 60 secondi solo per questa chiamata
                });
            return response.data;
        }
        return false;
    }

    /**
     * Salva un singolo elemento.
     */
    async saveTranslateItem(data: CertificationTranDTO | EducationTranDTO | ProjectTranDTO | SkillTranDTO | WorkExperienceTranDTO, sectionType: SectionTypes): Promise<boolean> {
        let url: string = '';
        switch (sectionType) {
            case SectionTypes.CERTIFICATION:
                url = '/translate/certification/save';
                break;
            case SectionTypes.EDUCATION:
                url = '/translate/education/save';
                break;
            case SectionTypes.EXPERIENCE:
                url = '/translate/workexperience/save';
                break;
            case SectionTypes.SKILL:
                url = '/translate/skill/save';
                break;
            case SectionTypes.PROJECT:
                url = '/translate/project/save';
                break;
        }

        if (url != '') {
            const response: AxiosResponse<boolean> =
                await this.api.post(url, data);
            return response.data;
        }
        return false;
    }

    /**
     * Elimina la traduzione di una lingua per un determinato elemento
     */
    async deleteTranslateItem(itemId: string, sectionType: SectionTypes, lang: string): Promise<boolean> {
        let url: string = '';
        switch (sectionType) {
            case SectionTypes.CERTIFICATION:
                url = '/translate/certification/';
                break;
            case SectionTypes.EDUCATION:
                url = '/translate/education/';
                break;
            case SectionTypes.EXPERIENCE:
                url = '/translate/workexperience/';
                break;
            case SectionTypes.SKILL:
                url = '/translate/skill/';
                break;
            case SectionTypes.PROJECT:
                url = '/translate/project/';
                break;
        }

        url = url + itemId + '/' + lang;

        if (url != '') {
            const response: AxiosResponse<boolean> =
                await this.api.delete(url);
            return response.data;
        }
        return false;
    }

    /**
     * Traduce tutti gli elementi di una sezione in bulk.
     * POST /api/translations/translate-bulk
     */
    async translateBulk(data: TranslateBulkDTO): Promise<void> {
        await this.api.post('/translations/translate-bulk', data);
    }

    /**
     * Recupera le lingue già tradotte per un singolo elemento.
     * GET /api/translations/{sectionType}/{itemId}/languages
     */
    async getTranslatedLanguages(
        sectionType: SectionTypes,
        itemId: number | string
    ): Promise<string[]> {
        const response: AxiosResponse<string[]> =
            await this.api.get(`/translations/${sectionType}/${itemId}/languages`);
        return response.data;
    }

    /**
     * Recupera le lingue già tradotte per tutti gli elementi di una sezione.
     * GET /api/translate/{sectionType}/languages
     * Risposta attesa dal backend: { translations: { "1": ["en", "fr"], "2": ["en"] } }
     */
    async getAllTranslatedLanguages(
        sectionType: SectionTypes
    ): Promise<Record<string, string[]>> {
        const response: AxiosResponse<TranslatedLanguagesMapDTO> =
            await this.api.get(`/translate/getsection/${sectionType}/languages`);
        return response.data.translations;
    }

    async getTranslatedItem(sectionId: string, sectionType: SectionTypes, langCode: string): Promise<EducationTranDTO | CertificationTranDTO | ProjectTranDTO | SkillTranDTO | WorkExperienceTranDTO | null | undefined> {
        let url: string = '';
        switch (sectionType) {
            case SectionTypes.CERTIFICATION:
                url = '/translate/certification';
                break;
            case SectionTypes.EDUCATION:
                url = '/translate/education';
                break;
            case SectionTypes.EXPERIENCE:
                url = '/translate/workexperience';
                break;
            case SectionTypes.SKILL:
                url = '/translate/skill';
                break;
            case SectionTypes.PROJECT:
                url = '/translate/project';
                break;
        }

        url = url + '/' + sectionId + '/' + langCode;

        if (url !== '') {
            try {
                const response: AxiosResponse<EducationTranDTO | CertificationTranDTO | ProjectTranDTO | SkillTranDTO | WorkExperienceTranDTO> =
                    await this.api.get(url);
                return response.data;
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 404) {
                    return null;
                }
                throw error; // rilancia gli altri errori
            }
        }
        return undefined;
    }
}